import React, { useState, useEffect, useMemo } from 'react';
import {
  Settings,
  Plus,
  Edit,
  Trash2,
  Save,
  X,
  CheckCircle,
  XCircle,
  ChevronUp,
  ChevronDown,
  Info,
  RefreshCw,
  FileText,
  ExternalLink,
  Search,
  Hash,
  Upload,
  BookOpen,
  ChevronRight
} from 'lucide-react';
import api from '../../api';
import toast from 'react-hot-toast';
import { useProject } from '../../contexts/ProjectContext';
import ToolMetadataModal from './ToolMetadataModal';

interface WorkflowPlatform {
  type: string;
  name: string;
  description: string;
  defaultTools: string[];
}

interface WorkflowConfiguration {
  id: number;
  tenant_id: number | null;
  project_id: number | null;
  platform_type: string;
  is_enabled: boolean;
  priority_order: number;
  configuration: Record<string, any>;
  enabled_tools: string[];
  created_at: string;
  updated_at: string;
}

interface ToolMetadata {
  id?: number;
  platform_type: string;
  tool_name: string;
  description?: string;
  use_cases?: string[];
  license_info?: string;
  documentation_url?: string;
  ai_opportunities?: string;
  regulatory_notes?: string;
  is_active?: boolean;
}

interface ToolDoc {
  id: number;
  platform_type: string;
  document_name: string;
  file_size: number | null;
  created_at: string;
}

const WorkflowToolsAdmin: React.FC = () => {
  const { currentProject } = useProject();
  const [platforms, setPlatforms] = useState<WorkflowPlatform[]>([]);
  const [configurations, setConfigurations] = useState<WorkflowConfiguration[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingConfig, setEditingConfig] = useState<WorkflowConfiguration | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedPlatformType, setSelectedPlatformType] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [priorityEditConfig, setPriorityEditConfig] = useState<WorkflowConfiguration | null>(null);
  const [newPriorityValue, setNewPriorityValue] = useState<string>('');

  // Tool Documentation state
  const [toolDocs, setToolDocs] = useState<ToolDoc[]>([]);
  const [showDocSection, setShowDocSection] = useState(false);
  const [docUploadPlatform, setDocUploadPlatform] = useState('');
  const [docUploadFile, setDocUploadFile] = useState<File | null>(null);
  const [uploadingDoc, setUploadingDoc] = useState(false);

  useEffect(() => {
    loadData();
    loadToolDocs();
  }, [currentProject]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [platformsRes, configsRes] = await Promise.all([
        api.get('/api/workflow-tools/platforms'),
        // Don't pass any params - will fetch global configurations
        api.get('/api/workflow-tools/configurations')
      ]);

      if (platformsRes.data.success) {
        setPlatforms(platformsRes.data.data);
      }

      if (configsRes.data.success) {
        setConfigurations(configsRes.data.data);
      }
    } catch (error: any) {
      console.error('Error loading workflow tools data:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to load workflow tools configuration';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const loadToolDocs = async () => {
    try {
      const res = await api.get('/api/workflow-tool-docs');
      if (res.data.success) setToolDocs(res.data.data);
    } catch (e) {
      console.error('Error loading tool docs:', e);
    }
  };

  const handleUploadToolDoc = async () => {
    if (!docUploadPlatform || !docUploadFile) {
      toast.error('Please select a platform and a file');
      return;
    }
    setUploadingDoc(true);
    try {
      const formData = new FormData();
      formData.append('file', docUploadFile);
      formData.append('platform_type', docUploadPlatform);
      await api.post('/api/workflow-tool-docs/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Documentation uploaded successfully');
      setDocUploadFile(null);
      setDocUploadPlatform('');
      loadToolDocs();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Upload failed');
    } finally {
      setUploadingDoc(false);
    }
  };

  const handleDeleteToolDoc = async (id: number) => {
    if (!confirm('Remove this documentation?')) return;
    try {
      await api.delete(`/api/workflow-tool-docs/${id}`);
      toast.success('Documentation removed');
      loadToolDocs();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Delete failed');
    }
  };

  const handleToggleEnabled = async (config: WorkflowConfiguration) => {
    try {
      const response = await api.put(`/api/workflow-tools/configurations/${config.id}`, {
        is_enabled: !config.is_enabled
      });

      if (response.data.success) {
        toast.success(`${config.platform_type} ${!config.is_enabled ? 'enabled' : 'disabled'}`);
        loadData();
      }
    } catch (error: any) {
      console.error('Error toggling platform:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to update platform status';
      toast.error(errorMessage);
    }
  };

  const handleUpdatePriority = async (config: WorkflowConfiguration, direction: 'up' | 'down') => {
    try {
      const sortedConfigs = [...configurations].sort((a, b) => a.priority_order - b.priority_order);
      const currentIndex = sortedConfigs.findIndex(c => c.id === config.id);

      if (direction === 'up' && currentIndex === 0) return;
      if (direction === 'down' && currentIndex === sortedConfigs.length - 1) return;

      const swapIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
      const swapConfig = sortedConfigs[swapIndex];

      const newPriority = swapConfig.priority_order;

      const optimisticUpdate = configurations.map(c => {
        if (c.id === config.id) return { ...c, priority_order: newPriority };
        if (c.id === swapConfig.id) return { ...c, priority_order: config.priority_order };
        return c;
      });
      setConfigurations(optimisticUpdate);

      const response = await api.put(`/api/workflow-tools/configurations/${config.id}`, {
        priority_order: newPriority
      });

      if (response.data.success) {
        await loadData();
      }
    } catch (error: any) {
      console.error('Error updating priority:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to update priority';
      toast.error(errorMessage);
      await loadData();
    }
  };

  const handleSaveTools = async (config: WorkflowConfiguration, tools: string[]) => {
    try {
      const response = await api.put(`/api/workflow-tools/configurations/${config.id}`, {
        enabled_tools: tools
      });

      if (response.data.success) {
        toast.success('Enabled tools updated');
        setEditingConfig(null);
        loadData();
      }
    } catch (error: any) {
      console.error('Error updating tools:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to update tools';
      toast.error(errorMessage);
    }
  };

  const handleAddConfiguration = async () => {
    if (!selectedPlatformType) {
      toast.error('Please select a platform type');
      return;
    }

    try {
      const platform = platforms.find(p => p.type === selectedPlatformType);
      if (!platform) {
        toast.error('Invalid platform type');
        return;
      }

      const response = await api.post('/api/workflow-tools/configurations', {
        platform_type: selectedPlatformType,
        is_enabled: false,
        enabled_tools: platform.defaultTools
        // Don't pass tenant_id or project_id - make it global for all projects
      });

      if (response.data.success) {
        toast.success(`${platform.name} added successfully`);
        setShowAddModal(false);
        setSelectedPlatformType('');
        loadData();
      }
    } catch (error: any) {
      console.error('Error adding configuration:', error);
      toast.error(error.response?.data?.message || 'Failed to add platform');
    }
  };

  const handleDeleteConfiguration = async (config: WorkflowConfiguration) => {
    if (!confirm(`Are you sure you want to delete the ${config.platform_type} configuration?`)) {
      return;
    }

    try {
      const response = await api.delete(`/api/workflow-tools/configurations/${config.id}`);

      if (response.data.success) {
        toast.success('Configuration deleted');
        loadData();
      }
    } catch (error: any) {
      console.error('Error deleting configuration:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to delete configuration';
      toast.error(errorMessage);
    }
  };

  const getPlatformName = (type: string) => {
    return platforms.find(p => p.type === type)?.name || type;
  };

  const getPlatformTools = (type: string) => {
    return platforms.find(p => p.type === type)?.defaultTools || [];
  };

  // Filter configurations based on search query
  const filteredConfigurations = useMemo(() => {
    if (!searchQuery.trim()) return configurations;

    const query = searchQuery.toLowerCase().trim();
    return configurations.filter(config => {
      const platformName = getPlatformName(config.platform_type).toLowerCase();
      const platformType = config.platform_type.toLowerCase();
      const tools = config.enabled_tools.join(' ').toLowerCase();

      return platformName.includes(query) ||
             platformType.includes(query) ||
             tools.includes(query);
    });
  }, [configurations, searchQuery, platforms]);

  // Handle direct priority update
  const handleSetPriority = async (config: WorkflowConfiguration, newPriority: number) => {
    if (newPriority < 1) {
      toast.error('Priority must be at least 1');
      return;
    }

    try {
      const response = await api.put(`/api/workflow-tools/configurations/${config.id}`, {
        priority_order: newPriority
      });

      if (response.data.success) {
        toast.success(`Priority updated to ${newPriority}`);
        setPriorityEditConfig(null);
        setNewPriorityValue('');
        await loadData();
      }
    } catch (error: any) {
      console.error('Error updating priority:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to update priority';
      toast.error(errorMessage);
    }
  };

  const openPriorityEditor = (config: WorkflowConfiguration) => {
    setPriorityEditConfig(config);
    setNewPriorityValue(config.priority_order.toString());
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="bg-red-50 border-2 border-red-200 rounded-lg p-6">
          <div className="flex items-start space-x-3">
            <X className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-red-900 mb-2">Failed to Load Workflow Tools</h3>
              <p className="text-red-700 mb-4">{error}</p>
              <button
                onClick={loadData}
                className="px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:shadow-lg transition-shadow flex items-center space-x-2"
              >
                <RefreshCw className="w-4 h-4" />
                <span>Retry</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Workflow Automation Platforms</h2>
          <p className="text-gray-600 mt-1">
            Configure which automation platforms are available for process optimization recommendations
          </p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:shadow-lg transition-shadow flex items-center space-x-2"
        >
          <Plus className="w-4 h-4" />
          <span>Add Platform</span>
        </button>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
        <div className="flex items-start space-x-3">
          <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-gray-700">
            <p className="font-semibold mb-2 text-gray-900">⚠️ Important: Workflow platforms must be configured here</p>
            <ul className="list-disc list-inside space-y-1 text-gray-600">
              <li><span className="text-blue-600 font-medium">At least one platform must be enabled</span> for Process Optimization to work</li>
              <li>Priority order determines the default platform (lower number = higher priority)</li>
              <li>Configure which specific tools are enabled within each platform</li>
              <li>Global configurations apply across all projects unless overridden</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Search Input */}
      {configurations.length > 0 && (
        <div className="relative mb-4">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search platforms by name, type, or tools..."
            className="block w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm bg-white shadow-sm"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
            >
              <X className="h-5 w-5" />
            </button>
          )}
        </div>
      )}

      {/* Search Results Info */}
      {searchQuery && (
        <div className="mb-3 text-sm text-gray-600">
          Showing {filteredConfigurations.length} of {configurations.length} platforms
          {filteredConfigurations.length === 0 && (
            <span className="text-orange-600 ml-2">— No matching platforms found</span>
          )}
        </div>
      )}

      {configurations.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-lg border-2 border-gray-200 shadow-sm">
          <Settings className="w-20 h-20 text-blue-500 mx-auto mb-6" />
          <p className="text-gray-900 font-bold text-xl mb-3">⚠️ No Workflow Platforms Configured</p>
          <p className="text-gray-600 mb-6 max-w-md mx-auto text-base leading-relaxed">
            Process Optimization feature requires at least one workflow automation platform to be configured.
            Please add a platform to enable automation recommendations.
          </p>
          <button
            onClick={() => setShowAddModal(true)}
            className="mt-4 px-8 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:shadow-lg transition-shadow font-semibold"
          >
            <Plus className="w-5 h-5 inline-block mr-2" />
            Configure Your First Platform
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredConfigurations
            .sort((a, b) => a.priority_order - b.priority_order)
            .map((config) => (
              <div
                key={config.id}
                className={`bg-white border ${
                  config.is_enabled ? 'border-blue-300' : 'border-gray-200'
                } rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow`}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <div className="flex flex-col space-y-1">
                      <button
                        onClick={() => handleUpdatePriority(config, 'up')}
                        disabled={config.priority_order === 1}
                        className="text-gray-500 hover:text-blue-600 disabled:opacity-30 disabled:cursor-not-allowed"
                        title="Move up"
                      >
                        <ChevronUp className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleUpdatePriority(config, 'down')}
                        disabled={
                          config.priority_order >= Math.max(...configurations.map(c => c.priority_order))
                        }
                        className="text-gray-500 hover:text-blue-600 disabled:opacity-30 disabled:cursor-not-allowed"
                        title="Move down"
                      >
                        <ChevronDown className="w-4 h-4" />
                      </button>
                    </div>

                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {getPlatformName(config.platform_type)}
                        </h3>
                        <span className={`px-2 py-0.5 text-xs font-medium rounded ${
                          config.is_enabled
                            ? 'bg-green-100 text-green-700'
                            : 'bg-red-100 text-red-700'
                        }`}>
                          {config.is_enabled ? 'Enabled' : 'Disabled'}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 flex items-center gap-1">
                        <span>Priority:</span>
                        <button
                          onClick={() => openPriorityEditor(config)}
                          className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 hover:bg-blue-100 text-gray-700 hover:text-blue-700 rounded transition-colors font-medium"
                          title="Click to set priority directly"
                        >
                          <Hash className="w-3 h-3" />
                          {config.priority_order}
                        </button>
                        <span className="mx-1">|</span>
                        <span>{config.enabled_tools.length} tools enabled</span>
                        {config.enabled_tools.length === 0 && (
                          <span className="text-orange-600 font-medium"> - Click "Edit Tools" to add tools</span>
                        )}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() =>
                        setEditingConfig(editingConfig?.id === config.id ? null : config)
                      }
                      className="px-3 py-1.5 text-sm font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors flex items-center gap-1.5"
                    >
                      <Edit className="w-4 h-4" />
                      <span>Edit Tools</span>
                    </button>

                    <button
                      onClick={() => handleToggleEnabled(config)}
                      className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                        config.is_enabled
                          ? 'bg-red-100 text-red-700 hover:bg-red-200'
                          : 'bg-green-100 text-green-700 hover:bg-green-200'
                      }`}
                      title={config.is_enabled ? 'Click to disable' : 'Click to enable'}
                    >
                      {config.is_enabled ? 'Disable' : 'Enable'}
                    </button>

                    <button
                      onClick={() => handleDeleteConfiguration(config)}
                      className="p-2 text-gray-600 hover:text-red-600 transition-colors rounded-lg hover:bg-gray-100"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {editingConfig?.id === config.id && (
                  <EditToolsPanel
                    config={config}
                    availableTools={getPlatformTools(config.platform_type)}
                    onSave={(tools) => handleSaveTools(config, tools)}
                    onCancel={() => setEditingConfig(null)}
                  />
                )}
              </div>
            ))}
        </div>
      )}

      {showAddModal && (
        <AddPlatformModal
          platforms={platforms}
          existingTypes={configurations.map(c => c.platform_type)}
          selectedType={selectedPlatformType}
          onSelectType={setSelectedPlatformType}
          onAdd={handleAddConfiguration}
          onClose={() => {
            setShowAddModal(false);
            setSelectedPlatformType('');
          }}
        />
      )}

      {/* Tool Documentation Section */}
      <div className="mt-8 border border-gray-200 rounded-lg overflow-hidden">
        <button
          onClick={() => { setShowDocSection(!showDocSection); if (!showDocSection) loadToolDocs(); }}
          className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
        >
          <div className="flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-indigo-600" />
            <span className="font-semibold text-gray-900">Tool Documentation</span>
            <span className="text-xs text-gray-500 ml-1">({toolDocs.length} uploaded)</span>
          </div>
          <ChevronRight className={`w-5 h-5 text-gray-500 transition-transform ${showDocSection ? 'rotate-90' : ''}`} />
        </button>

        {showDocSection && (
          <div className="p-4 space-y-4">
            <p className="text-sm text-gray-600">
              Upload platform documentation (PDF, DOCX, TXT) here. When users generate SOPs, they can compare against these docs to get inline markers like <strong>[Incorporated from Workday documentation]</strong>.
            </p>

            {/* Upload form */}
            <div className="flex flex-col sm:flex-row gap-3 p-3 bg-indigo-50 rounded-lg border border-indigo-200">
              <select
                value={docUploadPlatform}
                onChange={e => setDocUploadPlatform(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white"
              >
                <option value="">Select platform...</option>
                {platforms.map(p => (
                  <option key={p.type} value={p.type}>{p.name}</option>
                ))}
                <option value="workday">Workday</option>
                <option value="salesforce">Salesforce</option>
                <option value="servicenow">ServiceNow</option>
                <option value="microsoft">Microsoft</option>
              </select>
              <label className="flex-1 flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg bg-white cursor-pointer hover:border-indigo-400 transition-colors text-sm text-gray-600">
                <Upload className="w-4 h-4 text-gray-400 flex-shrink-0" />
                <span className="truncate">{docUploadFile ? docUploadFile.name : 'Choose file (PDF, DOCX, TXT)...'}</span>
                <input
                  type="file"
                  accept=".pdf,.docx,.doc,.txt"
                  className="hidden"
                  onChange={e => setDocUploadFile(e.target.files?.[0] || null)}
                />
              </label>
              <button
                onClick={handleUploadToolDoc}
                disabled={uploadingDoc || !docUploadPlatform || !docUploadFile}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium flex items-center gap-1.5 whitespace-nowrap"
              >
                <Upload className="w-4 h-4" />
                {uploadingDoc ? 'Uploading...' : 'Upload'}
              </button>
            </div>

            {/* Uploaded docs list */}
            {toolDocs.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-4">No documentation uploaded yet.</p>
            ) : (
              <div className="space-y-2">
                {toolDocs.map(doc => (
                  <div key={doc.id} className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg">
                    <div className="flex items-center gap-3 min-w-0">
                      <FileText className="w-4 h-4 text-indigo-500 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{doc.document_name}</p>
                        <p className="text-xs text-gray-500 capitalize">
                          {doc.platform_type}
                          {doc.file_size ? ` · ${Math.round(doc.file_size / 1024)} KB` : ''}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleDeleteToolDoc(doc.id)}
                      className="p-1.5 text-gray-400 hover:text-red-600 transition-colors flex-shrink-0"
                      title="Remove"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Priority Edit Modal */}
      {priorityEditConfig && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-sm w-full mx-4 shadow-xl">
            <h3 className="text-xl font-bold text-gray-900 mb-2">Set Priority</h3>
            <p className="text-sm text-gray-600 mb-4">
              Set priority for <span className="font-semibold">{getPlatformName(priorityEditConfig.platform_type)}</span>
            </p>
            <p className="text-xs text-gray-500 mb-4">
              Lower number = higher priority. Currently there are {configurations.length} platforms configured.
            </p>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                New Priority (1-{configurations.length})
              </label>
              <input
                type="number"
                min="1"
                max={configurations.length}
                value={newPriorityValue}
                onChange={(e) => setNewPriorityValue(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    const val = parseInt(newPriorityValue);
                    if (!isNaN(val)) {
                      handleSetPriority(priorityEditConfig, val);
                    }
                  }
                }}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg font-semibold text-center"
                autoFocus
              />
            </div>

            {/* Quick priority buttons */}
            <div className="mb-4">
              <p className="text-xs text-gray-500 mb-2">Quick set:</p>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => handleSetPriority(priorityEditConfig, 1)}
                  className="px-3 py-1.5 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
                >
                  #1 (Top)
                </button>
                {configurations.length > 2 && (
                  <button
                    onClick={() => handleSetPriority(priorityEditConfig, Math.ceil(configurations.length / 2))}
                    className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    #{Math.ceil(configurations.length / 2)} (Middle)
                  </button>
                )}
                <button
                  onClick={() => handleSetPriority(priorityEditConfig, configurations.length)}
                  className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  #{configurations.length} (Last)
                </button>
              </div>
            </div>

            <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
              <button
                onClick={() => {
                  setPriorityEditConfig(null);
                  setNewPriorityValue('');
                }}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  const val = parseInt(newPriorityValue);
                  if (!isNaN(val)) {
                    handleSetPriority(priorityEditConfig, val);
                  } else {
                    toast.error('Please enter a valid number');
                  }
                }}
                className="px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:shadow-lg transition-shadow font-medium"
              >
                Set Priority
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

interface EditToolsPanelProps {
  config: WorkflowConfiguration;
  availableTools: string[];
  onSave: (tools: string[]) => void;
  onCancel: () => void;
}

const EditToolsPanel: React.FC<EditToolsPanelProps> = ({
  config,
  availableTools,
  onSave,
  onCancel
}) => {
  const [selectedTools, setSelectedTools] = useState<string[]>(config.enabled_tools);
  const [customToolName, setCustomToolName] = useState<string>('');
  const [managingToolMetadata, setManagingToolMetadata] = useState<string | null>(null);

  const toggleTool = (tool: string) => {
    setSelectedTools(prev =>
      prev.includes(tool) ? prev.filter(t => t !== tool) : [...prev, tool]
    );
  };

  const addCustomTool = () => {
    const trimmed = customToolName.trim();
    if (trimmed && !selectedTools.includes(trimmed)) {
      setSelectedTools(prev => [...prev, trimmed]);
      setCustomToolName('');
    }
  };

  const removeCustomTool = (tool: string) => {
    setSelectedTools(prev => prev.filter(t => t !== tool));
  };

  const customTools = selectedTools.filter(t => !availableTools.includes(t));
  const allTools = [...availableTools, ...customTools];

  return (
    <div className="mt-4 pt-4 border-t border-gray-200">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-semibold text-gray-900">Enabled Tools</h4>
        <button
          onClick={() => setManagingToolMetadata('_add_new')}
          className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-1.5 font-medium"
        >
          <Plus className="w-4 h-4" />
          Add New Tool
        </button>
      </div>

      {allTools.length > 0 ? (
        <div className="mb-4">
          <p className="text-xs font-medium text-gray-700 mb-2">Select tools to enable:</p>
          <div className="grid grid-cols-2 gap-2">
            {allTools.map(tool => (
              <div key={tool} className="flex items-center gap-1">
                <label className="flex-1 flex items-center space-x-2 p-2.5 rounded-lg bg-gray-50 hover:bg-gray-100 cursor-pointer transition-colors border border-gray-200">
                  <input
                    type="checkbox"
                    checked={selectedTools.includes(tool)}
                    onChange={() => toggleTool(tool)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">{tool}</span>
                </label>
                <button
                  onClick={() => setManagingToolMetadata(tool)}
                  className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                  title="Manage tool details"
                >
                  <FileText className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="mb-4 text-center py-6 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <p className="text-sm text-gray-600">No tools added yet. Click "Add New Tool" to get started.</p>
        </div>
      )}

      <div className="mb-4 pb-4 border-t border-gray-200 pt-4">
        <p className="text-xs font-medium text-gray-700 mb-2">
          Quick add tool by name (will appear in list above):
        </p>
        <div className="flex gap-2">
          <input
            type="text"
            value={customToolName}
            onChange={(e) => setCustomToolName(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && addCustomTool()}
            placeholder="Enter tool name (e.g., Zapier, Custom API)"
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
          />
          <button
            onClick={addCustomTool}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm whitespace-nowrap"
          >
            Add Tool
          </button>
        </div>
      </div>
      <div className="flex items-center space-x-2">
        <button
          onClick={() => onSave(selectedTools)}
          className="px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:shadow-lg transition-shadow flex items-center space-x-1.5 text-sm font-medium"
        >
          <Save className="w-4 h-4" />
          <span>Save</span>
        </button>
        <button
          onClick={onCancel}
          className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors flex items-center space-x-1.5 text-sm font-medium"
        >
          <X className="w-4 h-4" />
          <span>Cancel</span>
        </button>
      </div>

      {managingToolMetadata && (
        <ToolMetadataModal
          platformType={config.platform_type}
          toolName={managingToolMetadata === '_add_new' ? '' : managingToolMetadata}
          onClose={() => setManagingToolMetadata(null)}
          onSaved={(toolName) => {
            if (!selectedTools.includes(toolName)) {
              setSelectedTools(prev => [...prev, toolName]);
            }
            setManagingToolMetadata(null);
          }}
        />
      )}
    </div>
  );
};

interface AddPlatformModalProps {
  platforms: WorkflowPlatform[];
  existingTypes: string[];
  selectedType: string;
  onSelectType: (type: string) => void;
  onAdd: () => void;
  onClose: () => void;
}

const AddPlatformModal: React.FC<AddPlatformModalProps> = ({
  platforms,
  existingTypes,
  selectedType,
  onSelectType,
  onAdd,
  onClose
}) => {
  const availablePlatforms = platforms.filter(p => !existingTypes.includes(p.type));

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-6 max-w-lg w-full mx-4 shadow-xl">
        <h3 className="text-2xl font-bold text-gray-900 mb-4">Add Workflow Platform</h3>

        {availablePlatforms.length === 0 ? (
          <p className="text-gray-600 text-sm mb-4">
            All available platforms have been configured.
          </p>
        ) : (
          <>
            <p className="text-gray-600 text-sm mb-4">
              Select a platform to configure for workflow automation recommendations:
            </p>

            <div className="space-y-2 mb-6 max-h-96 overflow-y-auto">
              {availablePlatforms.map(platform => (
                <label
                  key={platform.type}
                  className={`flex items-start space-x-3 p-4 rounded-lg cursor-pointer transition-all ${
                    selectedType === platform.type
                      ? 'bg-blue-50 border-2 border-blue-500'
                      : 'bg-gray-50 border border-gray-200 hover:border-blue-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="platform"
                    value={platform.type}
                    checked={selectedType === platform.type}
                    onChange={() => onSelectType(platform.type)}
                    className="mt-1 text-blue-600 focus:ring-blue-500"
                  />
                  <div className="flex-1">
                    <div className="font-semibold text-gray-900 text-base">{platform.name}</div>
                    <div className="text-sm text-gray-600 mt-1">{platform.description}</div>
                    <div className="text-xs text-gray-500 mt-2">
                      <span className="font-medium">Default tools:</span> {platform.defaultTools.join(', ')}
                    </div>
                  </div>
                </label>
              ))}
            </div>
          </>
        )}

        <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-5 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
          >
            Cancel
          </button>
          {availablePlatforms.length > 0 && (
            <button
              onClick={onAdd}
              disabled={!selectedType}
              className="px-5 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:shadow-lg transition-shadow disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              Add Platform
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default WorkflowToolsAdmin;
